import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  useAuth,
  useChangePassword,
  useUpdateProfile,
  useTherapistProfile,
  useStripeStatus,
  useWebAuthnRegister,
  isWebAuthnSupported,
} from '@theraply/api-client';
import {
  UserCircleIcon,
  BellIcon,
  CalendarIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  FingerPrintIcon,
} from '@heroicons/react/24/outline';

type SettingsTab = 'profile' | 'notifications' | 'availability' | 'payments' | 'security';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const tabs: { key: SettingsTab; label: string; icon: React.ReactNode }[] = [
    { key: 'profile', label: 'Profile', icon: <UserCircleIcon className="h-5 w-5" /> },
    { key: 'notifications', label: 'Notifications', icon: <BellIcon className="h-5 w-5" /> },
    { key: 'availability', label: 'Availability', icon: <CalendarIcon className="h-5 w-5" /> },
    { key: 'payments', label: 'Payments', icon: <CreditCardIcon className="h-5 w-5" /> },
    { key: 'security', label: 'Security', icon: <ShieldCheckIcon className="h-5 w-5" /> },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your account and practice settings.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Navigation */}
        <nav className="lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow">
            <ul className="divide-y divide-gray-100">
              {tabs.map((tab) => (
                <li key={tab.key}>
                  <button
                    onClick={() => setActiveTab(tab.key)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.key
                        ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-50 border-l-4 border-transparent'
                    }`}
                  >
                    <span className={activeTab === tab.key ? 'text-blue-600' : 'text-gray-400'}>
                      {tab.icon}
                    </span>
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === 'profile' && <ProfileSection />}
          {activeTab === 'notifications' && <NotificationsSection />}
          {activeTab === 'availability' && <AvailabilitySection />}
          {activeTab === 'payments' && <PaymentsSection />}
          {activeTab === 'security' && <SecuritySection />}
        </div>
      </div>
    </div>
  );
}

function ProfileSection() {
  const { user } = useAuth();
  const { data: profile, isLoading, error } = useTherapistProfile();
  const updateProfile = useUpdateProfile();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    bio: '',
    specialties: '',
    sessionRateCents: 0,
  });
  const [saveSuccess, setSaveSuccess] = useState(false);

  const startEditing = () => {
    setFormData({
      firstName: profile?.firstName || user?.firstName || '',
      lastName: profile?.lastName || user?.lastName || '',
      bio: profile?.bio || '',
      specialties: Array.isArray(profile?.specialties)
        ? profile.specialties.join(', ')
        : profile?.specialties || '',
      sessionRateCents: profile?.sessionRateCents || 15000,
    });
    setEditing(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        firstName: formData.firstName,
        lastName: formData.lastName,
        bio: formData.bio,
        specialties: formData.specialties.split(',').map((s) => s.trim()).filter(Boolean),
        sessionRateCents: formData.sessionRateCents,
      });
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
    }
  };

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-sm text-red-700">Failed to load profile information.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
        {!editing && (
          <button
            onClick={startEditing}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Edit Profile
          </button>
        )}
      </div>

      {saveSuccess && (
        <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 text-green-500" />
          <span className="text-sm text-green-700">Profile updated successfully.</span>
        </div>
      )}

      <div className="p-6 space-y-6">
        {editing ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, bio: e.target.value }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Tell clients about your practice and approach..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specialties (comma-separated)
              </label>
              <input
                type="text"
                value={formData.specialties}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, specialties: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Anxiety, Depression, CBT, Couples Therapy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Rate ($)
              </label>
              <input
                type="number"
                value={(formData.sessionRateCents / 100).toFixed(2)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sessionRateCents: Math.round(parseFloat(e.target.value || '0') * 100),
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="0.01"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={updateProfile.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
            {updateProfile.isError && (
              <p className="text-sm text-red-600">Failed to save. Please try again.</p>
            )}
          </>
        ) : (
          <>
            <InfoRow label="Name" value={`${profile?.firstName || user?.firstName || ''} ${profile?.lastName || user?.lastName || ''}`} />
            <InfoRow label="Email" value={user?.email || '--'} />
            <InfoRow label="Bio" value={profile?.bio || 'Not set'} />
            <InfoRow
              label="Specialties"
              value={
                Array.isArray(profile?.specialties) && profile.specialties.length > 0
                  ? profile.specialties.join(', ')
                  : 'Not set'
              }
            />
            <InfoRow
              label="Session Rate"
              value={
                profile?.sessionRateCents
                  ? `$${(profile.sessionRateCents / 100).toFixed(2)}`
                  : 'Not set'
              }
            />
          </>
        )}
      </div>
    </div>
  );
}

function NotificationsSection() {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
        <p className="mt-1 text-sm text-gray-500">
          Manage how and when you receive notifications.
        </p>
      </div>
      <div className="p-6">
        <Link
          to="/settings/notifications"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <BellIcon className="h-4 w-4 mr-2" />
          Open Notification Settings
        </Link>
        <p className="mt-3 text-sm text-gray-500">
          Configure email and SMS notification preferences, session reminders, and booking alerts.
        </p>
      </div>
    </div>
  );
}

function AvailabilitySection() {
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Availability</h2>
        <p className="mt-1 text-sm text-gray-500">
          Set your weekly schedule and manage time off.
        </p>
      </div>
      <div className="p-6">
        <Link
          to="/availability"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <CalendarIcon className="h-4 w-4 mr-2" />
          Manage Availability
        </Link>
        <p className="mt-3 text-sm text-gray-500">
          Configure your weekly availability slots and manage time-off exceptions. Clients will see these times when booking sessions.
        </p>
      </div>
    </div>
  );
}

function PaymentsSection() {
  const { getStatus, status, loading } = useStripeStatus();

  useEffect(() => {
    getStatus().catch(() => {});
  }, []);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Payments</h2>
        <p className="mt-1 text-sm text-gray-500">
          Manage your Stripe account and payout settings.
        </p>
      </div>
      <div className="p-6 space-y-4">
        {loading ? (
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            <span className="text-sm text-gray-500">Loading payment status...</span>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <CreditCardIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">Stripe Connect Status</span>
            </div>
            <div className="space-y-2">
              <StatusRow
                label="Account Connected"
                connected={status?.connected ?? false}
              />
              <StatusRow
                label="Charges Enabled"
                connected={status?.chargesEnabled ?? false}
              />
              <StatusRow
                label="Payouts Enabled"
                connected={status?.payoutsEnabled ?? false}
              />
            </div>
          </div>
        )}

        <Link
          to="/payments"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <CreditCardIcon className="h-4 w-4 mr-2" />
          Manage Payment Settings
        </Link>
      </div>
    </div>
  );
}

function SecuritySection() {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const changePassword = useChangePassword();
  const webAuthnRegister = useWebAuthnRegister();
  const webAuthnSupported = isWebAuthnSupported();
  const [passkeySuccess, setPasskeySuccess] = useState(false);

  const handleChangePassword = async () => {
    setPasswordError('');
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    try {
      await changePassword.mutateAsync({
        currentPassword,
        newPassword,
      });
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess(true);
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError('Failed to change password. Please check your current password.');
    }
  };

  const handleRegisterPasskey = async () => {
    try {
      await webAuthnRegister.mutateAsync({
        deviceName: `Passkey - ${new Date().toLocaleDateString()}`,
      });
      setPasskeySuccess(true);
      setTimeout(() => setPasskeySuccess(false), 3000);
    } catch (err) {
      console.error('Passkey registration failed:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Password */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Password</h2>
          <p className="mt-1 text-sm text-gray-500">
            Change your account password.
          </p>
        </div>
        <div className="p-6">
          {passwordSuccess && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <span className="text-sm text-green-700">Password changed successfully.</span>
            </div>
          )}

          {showPasswordForm ? (
            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {passwordError && (
                <p className="text-sm text-red-600">{passwordError}</p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleChangePassword}
                  disabled={changePassword.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {changePassword.isPending ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordForm(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <KeyIcon className="h-4 w-4 mr-2" />
              Change Password
            </button>
          )}
        </div>
      </div>

      {/* Passkeys / WebAuthn */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Passkeys</h2>
          <p className="mt-1 text-sm text-gray-500">
            Use biometric authentication (fingerprint, face ID) for secure, passwordless login.
          </p>
        </div>
        <div className="p-6">
          {passkeySuccess && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <span className="text-sm text-green-700">Passkey registered successfully.</span>
            </div>
          )}

          {webAuthnRegister.isError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
              <span className="text-sm text-red-700">Failed to register passkey. Please try again.</span>
            </div>
          )}

          {webAuthnSupported ? (
            <button
              onClick={handleRegisterPasskey}
              disabled={webAuthnRegister.isPending}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              <FingerPrintIcon className="h-4 w-4 mr-2" />
              {webAuthnRegister.isPending ? 'Registering...' : 'Register New Passkey'}
            </button>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Passkeys not supported
                  </p>
                  <p className="mt-1 text-sm text-yellow-700">
                    Your browser does not support WebAuthn/passkeys. Try using a modern browser like Chrome, Safari, or Firefox.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-sm text-gray-900">{value}</p>
    </div>
  );
}

function StatusRow({
  label,
  connected,
}: {
  label: string;
  connected: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span
        className={`flex items-center gap-1 font-medium ${
          connected ? 'text-green-600' : 'text-gray-400'
        }`}
      >
        {connected ? (
          <>
            <CheckCircleIcon className="h-4 w-4" /> Active
          </>
        ) : (
          'Not active'
        )}
      </span>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
      <div className="h-4 bg-gray-200 rounded w-1/4 animate-pulse" />
    </div>
  );
}
